import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { Button, IconButton, ProgressBar } from '../components';
import { springGentle } from '../tokens';
import { useWalkthrough } from './WalkthroughContext';

/**
 * Bottom-anchored coach panel. Sits above the TabBar (which the
 * navigator hides while active), but does NOT cover the upper portion
 * of the screen — the user can see whatever the step is pointing at.
 *
 * Animations: slide up from bottom on activate, slide down on dismiss.
 * Each step's content fades + lifts when stepIndex changes via a keyed
 * AnimatePresence inside the panel.
 */
export function WalkthroughPanel() {
  const { active, stepIndex, step, totalSteps, isLast, next, back, skip } =
    useWalkthrough();
  const Icon = step.icon;

  return (
    <AnimatePresence>
      {active ? (
        <motion.div
          key="walkthrough"
          role="dialog"
          aria-label={step.title}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={springGentle}
          className="fixed bottom-0 inset-x-0 z-40"
        >
          <div className="mx-auto max-w-content bg-surface rounded-t-sheet shadow-lg pb-[max(env(safe-area-inset-bottom),16px)]">
            {/* Drag handle visual — not actually draggable; reads as
                "this is a panel" without claiming the gesture. */}
            <div className="pt-3 pb-1 flex items-center justify-center">
              <div className="w-9 h-[5px] rounded-pill bg-text-tertiary/40" />
            </div>

            <div className="px-card-pad pt-2 pb-4">
              {/* Top bar: optional Back + step counter on the right */}
              <div className="flex items-center justify-between mb-3 min-h-tap">
                {stepIndex > 0 ? (
                  <IconButton
                    icon={<ChevronLeft size={20} strokeWidth={1.5} />}
                    label="Back"
                    onClick={back}
                  />
                ) : (
                  <div className="w-11 h-11" />
                )}
                <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
                  Step {stepIndex + 1} of {totalSteps}
                </span>
                <div className="w-11 h-11" />
              </div>

              {/* Animated body — keyed so the icon + copy crossfade
                  between steps without the chrome moving. */}
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-12 h-12 rounded-card bg-accent/10 inline-flex items-center justify-center text-accent mb-3">
                    <Icon size={28} strokeWidth={1.5} />
                  </div>
                  <h2 className="text-[20px] font-semibold tracking-tight text-text-primary">
                    {step.title}
                  </h2>
                  <p className="mt-1 text-[14px] leading-relaxed text-text-secondary">
                    {step.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="mt-4">
                <ProgressBar value={(stepIndex + 1) / totalSteps} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button variant="ghost" size="lg" onClick={skip}>
                  {isLast ? 'Close' : 'Skip'}
                </Button>
                <Button variant="primary" size="lg" onClick={next}>
                  {isLast ? 'Done' : 'Next'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
